<template>
  <v-container fluid>
    <v-row
      class="pa-3 ma-3"
      align="center"
      justify="center"
    >
      <v-col
        xs="10"
        sm="9"
        md="6"
        lg="4"
        xl="3"
      >
        <v-row align="center">
          <v-col cols="auto">
            <router-link to="/">
              <img
                src="@/assets/logo_black.png"
                alt="Home"
                height="24"
                class="mr-3"
              />
            </router-link>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="auto">
            <h3>Autogiro medgivandeformulär</h3>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row
      align="center"
      justify="center"
    >
      <v-col
        xs="10"
        sm="9"
        md="6"
        lg="4"
        xl="3"
      >
        <v-card
          elevation="0"
          variant="outlined"
          class="pa-3 mb-3"
        >
          <v-card-title class="mb-4">Betalningsmottagare</v-card-title>
          <v-card-text>
            <AttributeDisplay
              label="Namn"
              value="Visma Amili AB"
            />
            <AttributeDisplay
              label="Address"
              value="Redaregatan 50, 252 25 Helsingborg"
            />
            <AttributeDisplay
              label="Organisationsnummer"
              value="556439-5670"
            />
            <AttributeDisplay
              label="Bankgironummer"
              :value="amiliBankgiroNumber"
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      align="center"
      justify="center"
    >
      <v-col
        xs="10"
        sm="9"
        md="6"
        lg="4"
        xl="3"
      >
        <v-card
          elevation="0"
          variant="outlined"
          class="px-3 pt-3 pb-0 mb-3"
        >
          <v-card-title> Betalare</v-card-title>
          <v-form
            v-model="form"
            ref="vuetifyForm"
            class="px-3 pt-3 pb-0"
          >
            <InputField
              v-model="name"
              label="Namn"
              :rules="[required]"
            />
            <InputField
              v-model="address.address_line_1"
              label="Gatuadress"
            />
            <v-row justify="space-between">
              <v-col cols="6">
                <InputField
                  v-model="address.zip_code"
                  label="Postkod"
                  :rules="[minCharacter(5, 5), numbersOnly]"
                />
              </v-col>
              <v-col cols="6">
                <InputField
                  v-model="address.city"
                  label="Stad"
                />
              </v-col>
            </v-row>
            <InputField
              v-model="payerBankgiroNumber"
              label="Bankgironummer"
              :rules="[required, minCharacter(7, 8), numbersOnly]"
            />
            <InputField
              v-model="organizationNumber"
              label="Organisationsnummer"
              :rules="[required, minCharacter(10, 10), numbersOnly]"
            />
          </v-form>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      align="center"
      no-gutters
      justify="center"
    >
      <v-col
        xs="10"
        sm="9"
        md="6"
        lg="4"
        xl="3"
      >
        <v-card elevation="0">
          <v-card-title> Medgivande till betalning via Autogiro</v-card-title>
          <v-card-text>
            Undertecknad (”betalaren”), medger att betalning får göras genom uttag från det konto som är anslutet till
            angivet bankgironummer, på begäran av angiven betalningsmottagare för betalning till denne på viss dag
            (”förfallodagen”) via Autogiro.
          </v-card-text>
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-title> Villkor</v-expansion-panel-title>
              <v-expansion-panel-text>
                Betalaren samtycker till att behandling av personuppgifter som lämnats i detta medgivande behandlas av
                betalarens betaltjänstleverantör, betalningsmottagaren, betalningsmottagarens betaltjänstleverantör och
                Bankgirocentralen BGC AB för administration av tjänsten. Personuppgiftsansvariga för denna
                personuppgiftsbehandling är betalarens betaltjänstleverantör, betalningsmottagaren samt
                betalningsmottagarens betaltjänstleverantör. Betalaren kan när som helst begära att få tillgång till
                eller rättelse av personuppgifterna genom att kontakta betalarens betaltjänstleverantör. Ytterligare
                information om behandling av personuppgifter i samband med betalningar kan finnas i villkoren för kontot
                och i avtalet med betalningsmottagaren. Betalaren kan när som helst återkalla sitt samtycke, vilket
                medför att tjänsten i sin helhet avslutas.
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      justify="center"
      align="start"
    >
      <v-col
        xs="10"
        sm="9"
        md="6"
        lg="4"
        xl="3"
      >
        <v-btn
          color="primary"
          :block="xs"
          @click="openSigningDialog"
        >
          Signera
        </v-btn>
      </v-col>
    </v-row>
    <BBankIdDialog
      v-model="showDialog"
      mode="signing"
      @completed="onSigningCompleted"
      :signing-data="signingData"
      :create-signing-token-endpoint="createSigningTokenEndpoint"
      signing-visible-data="Du signerar för Autogiro hos Visma Amili AB"
      init-endpoint="/direct-debit-company-mobile-bankid-sign/"
      poll-endpoint="/direct-debit-company-consent-requests/{signing_token}"
      cancel-endpoint="/direct-debit-company-consent-requests/{signing_token}"
    />
  </v-container>
</template>

<script lang="ts" setup>
import AttributeDisplay from '@/components/AttributeDisplay.vue'
import InputField from '@/components/InputField.vue'
import BBankIdDialog from '@/components/BBankIdDialog/BBankIdDialog.vue'

import { computed, ref } from 'vue'
import type { VForm } from 'vuetify/components'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

interface Address {
  address_line_1: string
  zip_code: string
  city: string
  country: string
}

const address = ref<Address>({
  address_line_1: '',
  zip_code: '',
  city: '',
  country: 'SE',
})

const { xs } = useDisplay()
const router = useRouter()
const showDialog = ref(false)
const form = ref(false)
const vuetifyForm = ref<InstanceType<typeof VForm> | null>(null)
const name = ref('')
const amiliBankgiroNumber = ref('5642-1191')
const payerBankgiroNumber = ref('')
const organizationNumber = ref('')

const createSigningTokenEndpoint = `/direct-debit-company-consent-requests/create-signing-token/${
  import.meta.env.VITE_APP_NAME
}`

const signingData = computed(() => ({
  name: name.value,
  organization_no: cleanString(organizationNumber.value),
  bankgiro: cleanString(amiliBankgiroNumber.value),
  payer_number: cleanString(payerBankgiroNumber.value),
  address: {
    ...address.value,
    zip_code: cleanString(address.value.zip_code),
  },
}))

async function openSigningDialog() {
  if (!vuetifyForm.value) return

  const { valid } = await vuetifyForm.value.validate()

  if (valid) {
    showDialog.value = true
  }
}

function onSigningCompleted() {
  void router.push({ name: 'RegistrationCompleted' })
}

function required(value: string) {
  return !!value || 'Obligatoriskt fält'
}

function cleanString(value: string, ignoreChars = /[-.,\s]/g): string {
  return value.replace(ignoreChars, '')
}

function minCharacter(min: number, max: number) {
  return (value: string): true | string => {
    if (!value) {
      return true
    }
    const cleaned = cleanString(value)
    if (cleaned.length < min) {
      return `Minst ${min} siffror krävs`
    }
    if (cleaned.length > max) {
      return `Max ${max} siffror tillåtet`
    }
    return true
  }
}

function numbersOnly(value: string): true | string {
  if (!value) {
    return true
  }
  const cleaned = cleanString(value)
  const regex = /^\d*$/
  return regex.test(cleaned) ? true : 'Endast siffror är tillåtna'
}
</script>
